
<template>
  <div class="floating-window" :class="{ 'collapsed': isCollapsed }">
    <!-- 可伸缩按钮 -->
    <div class="toggle-button" @click="toggleWindow">
      <i class="toggle-icon" :class="isCollapsed ? 'collapsed' : 'expanded'"></i>
    </div>

    <transition name="slide-fade">
      <div v-if="!isCollapsed" class="floating-content" @click="openNewPage">
        <img :src="computedImageUrl" :alt="imageAlt" class="floating-image" />
        <div v-if="showTitle" class="floating-title">{{ title }}</div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Props
const props = defineProps({
  imageUrl: {
    type: String,
    default: '/diaocha.png'
  },
  imageAlt: {
    type: String,
    default: 'AI4Science需求调查'
  },
  targetUrl: {
    type: String,
    default: 'https://v.wjx.cn/vm/tGo634O.aspx#'
  },
  title: {
    type: String,
    default: ''
  },
  showTitle: {
    type: Boolean,
    default: false
  },
  width: {
    type: Number,
    default: 185
  },
  height: {
    type: Number,
    default: 355
  },
  position: {
    type: String,
    default: 'left-bottom', // left-bottom, right-bottom, left-top, right-top
    validator: (value) => ['left-bottom', 'right-bottom', 'left-top', 'right-top'].includes(value)
  },
  defaultCollapsed: {
    type: Boolean,
    default: false
  }
})

// 响应式数据
const isCollapsed = ref(props.defaultCollapsed)

// 计算属性
const computedImageUrl = computed(() => {
  if (props.imageUrl.startsWith('http') || props.imageUrl.startsWith('/')) {
    return props.imageUrl
  }
  // 如果是相对路径，添加assets前缀
  return new URL(`../assets/images/${props.imageUrl}`, import.meta.url).href
})

// 方法
const toggleWindow = () => {
  isCollapsed.value = !isCollapsed.value
}

const openNewPage = () => {
  if (props.targetUrl.startsWith('http')) {
    window.open(props.targetUrl, '_blank')
  } else {
    window.open(window.location.origin + props.targetUrl, '_blank')
  }
}
</script>

<style scoped>
.floating-window {
  position: fixed;
  z-index: 1000;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.floating-window {
  bottom: 20px;
  left: 10px;
}

.toggle-button {
  width: 20px;
  height: 60px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-radius: 0 15px 15px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  order: 1;
  user-select: none;
}

.toggle-button:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.toggle-button:active {
  transform: scale(0.98);
}

.toggle-icon {
  width: 0;
  height: 0;
  transition: all 0.3s ease;
}

.toggle-icon.expanded {
  border-right: 6px solid white;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.toggle-icon.collapsed {
  border-left: 6px solid white;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.floating-content {
  width: v-bind('props.width + "px"');
  height: v-bind('props.height + "px"');
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  margin-right: 5px;
  order: 0;
  position: relative;
}

.floating-content:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.floating-content:active {
  transform: translateY(-1px);
}

.floating-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.floating-content:hover .floating-image {
  transform: scale(1.02);
}

.floating-title {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: white;
  padding: 8px 6px 6px;
  font-size: 10px;
  text-align: center;
  border-radius: 0 0 12px 12px;
}

.collapsed .toggle-button {
  border-radius: 15px;
}

/* 过渡动画 */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  transform: translateX(-100%) scale(0.8);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-100%) scale(0.8);
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .floating-window {
    bottom: 15px;
    left: 15px;
  }

  .floating-content {
    width: v-bind('(props.width * 0.9) + "px"');
    height: v-bind('(props.height * 0.9) + "px"');
  }

  .toggle-button {
    width: 15px;
    height: 40px;
    font-size: 12px;
  }

  .toggle-icon.expanded {
    border-right-width: 5px;
    border-top-width: 3px;
    border-bottom-width: 3px;
  }

  .toggle-icon.collapsed {
    border-left-width: 5px;
    border-top-width: 3px;
    border-bottom-width: 3px;
  }
}

@media (max-width: 480px) {
  .floating-window {
    bottom: 10px;
    left: 10px;
  }

  .floating-content {
    width: v-bind('(props.width * 0.7) + "px"');
    height: v-bind('(props.height * 0.7) + "px"');
  }

  .toggle-button {
    width: 15px;
    height: 40px;
    font-size: 11px;
  }

  .floating-title {
    font-size: 9px;
    padding: 6px 4px 4px;
  }
}

/* 防止与其他固定元素冲突 */
@media (max-height: 600px) {
  .floating-window {
    bottom: 10px;
  }
}
</style>
